import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { forwardRef } from "react";
import { Skeleton } from "../Skeleton";
import styles from "./Checkbox.module.scss";
import type { CheckboxProps } from "./types";

const cx = classNames.bind(styles);

/**
 * An input component to allow toggle binary state
 */
export const Checkbox = forwardRef(
  (
    {
      value,
      label,
      onChange,
      name,
      isDisabled,
      isLoading,
      isBusy,
      isIndeterminate,
      isFocusable = true,
      className,
    }: CheckboxProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const labelId = name ? `${name}-label` : nanoid(6);

    const handleClick = () => {
      if (isDisabled) return;

      onChange?.(!value);
    };

    const handleKeyDown = ({ code }: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled || !onChange) {
        return;
      }

      switch (code) {
        case "Enter":
          onChange(!value);
      }
    };

    return (
      <div
        className={cx("container", { isDisabled }, className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        ref={ref}
      >
        <input
          type="checkbox"
          name={name}
          checked={value}
          onChange={() => onChange?.(!value)}
          disabled={isDisabled}
          className={cx("checkbox", { isIndeterminate })}
          aria-labelledby={labelId}
          tabIndex={isFocusable ? 0 : -1}
          readOnly={isFocusable}
        />

        <Skeleton
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cx("icon", { isIndeterminate })}
          >
            <path
              d={isIndeterminate ? "M4,8 h8" : "M4,8 L7,11 L12,5"}
              stroke="white"
              strokeWidth={isIndeterminate ? "3" : "2"}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Skeleton>

        {label ? (
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
          >
            <label
              id={labelId}
              htmlFor={name}
              className={cx("label", { isDisabled })}
            >
              {label}
            </label>
          </Skeleton>
        ) : null}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
